import React, { useState } from 'react';
import './CreatePlayerPage.css';
import { createPlayer, generatePortraitTrio, selectPortrait } from '../services/db';
import { generateRandomFace } from '../utils/faceGenerator';
import PortraitPicker from '../components/PortraitPicker';
import '../components/PortraitPicker.css';
import {
  ArrowLeft,
  ArrowRight,
  ChevronUp,
  ChevronDown,
  RefreshCw,
  Check,
  X,
  CircleDot,
  Coins,
} from 'lucide-react';

interface CreatePlayerPageProps {
  onNavigate?: (page: string) => void;
}

/* ─── Grade System ─── */
const getGrade = (val: number): string => {
  if (val >= 70) return 'A+';
  if (val >= 65) return 'A';
  if (val >= 60) return 'A-';
  if (val >= 57) return 'B+';
  if (val >= 53) return 'B';
  if (val >= 50) return 'B-';
  if (val >= 47) return 'C+';
  if (val >= 43) return 'C';
  if (val >= 40) return 'C-';
  if (val >= 35) return 'D+';
  if (val >= 30) return 'D';
  if (val >= 25) return 'D-';
  return 'F';
};

const getGradeColor = (val: number): string => {
  if (val >= 65) return '#14b8a6';
  if (val >= 50) return '#3b82f6';
  if (val >= 35) return '#f59e0b';
  return '#ef4444';
};

const randomStat = (min: number, max: number): number =>
  Math.floor(min + Math.random() * (max - min + 1));

/* ─── Position Data ─── */
const POS_GROUPS = [
  {
    label: 'Battery',
    positions: [
      { id: 'P', label: 'Pitcher' },
      { id: 'C', label: 'Catcher' },
    ],
  },
  {
    label: 'Infield',
    positions: [
      { id: '1B', label: '1st Base' },
      { id: '2B', label: '2nd Base' },
      { id: '3B', label: '3rd Base' },
      { id: 'SS', label: 'Shortstop' },
    ],
  },
  {
    label: 'Outfield',
    positions: [
      { id: 'LF', label: 'Left Field' },
      { id: 'CF', label: 'Center Field' },
      { id: 'RF', label: 'Right Field' },
    ],
  },
  {
    label: 'Bench',
    positions: [
      { id: 'DH', label: 'DH' },
    ],
  },
];

const GROUP_COLORS: Record<string, string> = {
  Battery: '#f97316',
  Infield: '#60a5fa',
  Outfield: '#4ade80',
  Bench: '#facc15',
};

const POSITIONS_FLAT = POS_GROUPS.flatMap(g => g.positions);

/* ─── Attribute Data ─── */
const HITTING_ATTRS = ['power', 'contact', 'speed', 'fielding', 'arm', 'discipline', 'endurance'];
const PITCHING_ATTRS = ['velocity', 'control', 'movement', 'deception', 'endurance'];
const HITTING_LABELS: Record<string, string> = {
  power: 'Power', contact: 'Contact', speed: 'Speed',
  fielding: 'Fielding', arm: 'Arm', discipline: 'Discipline', endurance: 'Endurance',
};
const PITCHING_LABELS: Record<string, string> = {
  velocity: 'Velocity', control: 'Control', movement: 'Movement',
  deception: 'Deception', endurance: 'Endurance',
};

function generateAttrs(position: string): Record<string, number> {
  const isPitcher = position === 'P';
  if (isPitcher) {
    return {
      power: randomStat(10, 25), contact: randomStat(10, 25), speed: randomStat(15, 30),
      fielding: randomStat(20, 35), arm: randomStat(25, 40), discipline: randomStat(15, 30),
      endurance: randomStat(25, 40), velocity: randomStat(30, 50), control: randomStat(25, 45),
      movement: randomStat(25, 45), deception: randomStat(25, 45),
    };
  }
  const templates: Record<string, () => Record<string, number>> = {
    'C':  () => ({ power: randomStat(20, 35), contact: randomStat(25, 40), speed: randomStat(15, 30), fielding: randomStat(30, 45), arm: randomStat(30, 45), discipline: randomStat(20, 35), endurance: randomStat(25, 40) }),
    '1B': () => ({ power: randomStat(30, 45), contact: randomStat(25, 40), speed: randomStat(15, 25), fielding: randomStat(15, 30), arm: randomStat(15, 25), discipline: randomStat(20, 35), endurance: randomStat(20, 35) }),
    'DH': () => ({ power: randomStat(30, 45), contact: randomStat(25, 40), speed: randomStat(15, 25), fielding: randomStat(10, 25), arm: randomStat(10, 25), discipline: randomStat(20, 35), endurance: randomStat(20, 35) }),
    '2B': () => ({ power: randomStat(15, 30), contact: randomStat(30, 45), speed: randomStat(30, 45), fielding: randomStat(30, 45), arm: randomStat(20, 35), discipline: randomStat(25, 40), endurance: randomStat(25, 40) }),
    'SS': () => ({ power: randomStat(15, 30), contact: randomStat(30, 45), speed: randomStat(30, 45), fielding: randomStat(30, 50), arm: randomStat(25, 40), discipline: randomStat(25, 40), endurance: randomStat(25, 40) }),
    '3B': () => ({ power: randomStat(30, 45), contact: randomStat(25, 40), speed: randomStat(20, 35), fielding: randomStat(25, 40), arm: randomStat(30, 45), discipline: randomStat(20, 35), endurance: randomStat(20, 35) }),
    'LF': () => ({ power: randomStat(25, 40), contact: randomStat(25, 40), speed: randomStat(30, 45), fielding: randomStat(25, 40), arm: randomStat(20, 35), discipline: randomStat(20, 35), endurance: randomStat(25, 40) }),
    'CF': () => ({ power: randomStat(20, 35), contact: randomStat(25, 40), speed: randomStat(35, 50), fielding: randomStat(30, 45), arm: randomStat(20, 35), discipline: randomStat(20, 35), endurance: randomStat(25, 40) }),
    'RF': () => ({ power: randomStat(30, 45), contact: randomStat(25, 40), speed: randomStat(25, 40), fielding: randomStat(25, 40), arm: randomStat(30, 50), discipline: randomStat(20, 35), endurance: randomStat(25, 40) }),
  };
  return (templates[position] || templates['1B'])();
}

const STARTING_POINTS = 15;
const MAX_PER_ATTR = 55;
const STEP_LABELS = ['Identity', 'Attributes', 'Review', 'Choose Look'];

/* ─── Component ─── */
const CreatePlayerPage: React.FC<CreatePlayerPageProps> = ({ onNavigate }) => {
  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [position, setPosition] = useState('');
  const [battingHand, setBattingHand] = useState('R');
  const [throwingHand, setThrowingHand] = useState('R');
  const [attrs, setAttrs] = useState<Record<string, number>>({});
  const [points, setPoints] = useState(0);
  const [baseAttrs, setBaseAttrs] = useState<Record<string, number>>({});
  const [created, setCreated] = useState(false);
  const [createdPlayer, setCreatedPlayer] = useState<any>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Portrait picker state
  const [portraitChoices, setPortraitChoices] = useState<string[]>([]);
  const [portraitSlots, setPortraitSlots] = useState<number[]>([]);
  const [portraitLoading, setPortraitLoading] = useState(false);
  const [portraitError, setPortraitError] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  const [selecting, setSelecting] = useState(false);
  const [rerollsUsed, setRerollsUsed] = useState(0);
  const [rerolling, setRerolling] = useState(false);
  const [portraitDone, setPortraitDone] = useState(false);

  const isPitcher = position === 'P';
  const attrList = isPitcher ? PITCHING_ATTRS : HITTING_ATTRS;
  const attrLabels = isPitcher ? PITCHING_LABELS : HITTING_LABELS;

  function handlePositionSelect(pos: string) {
    setPosition(pos);
    const newAttrs = generateAttrs(pos);
    setAttrs(newAttrs);
    setBaseAttrs(newAttrs);
    setPoints(STARTING_POINTS);
  }

  function adjustAttr(attr: string, dir: 'up' | 'down') {
    const val = attrs[attr] || 30;
    const base = baseAttrs[attr] || 30;
    if (dir === 'up' && points >= 3 && val + 3 <= MAX_PER_ATTR) {
      setAttrs(prev => ({ ...prev, [attr]: val + 3 }));
      setPoints(p => p - 3);
    } else if (dir === 'down' && val > base) {
      setAttrs(prev => ({ ...prev, [attr]: Math.max(base, val - 3) }));
      setPoints(p => p + 3);
    }
  }

  function reroll() {
    const newAttrs = generateAttrs(position);
    setAttrs(newAttrs);
    setBaseAttrs(newAttrs);
    setPoints(STARTING_POINTS);
  }

  function getOverall() {
    if (!Object.keys(attrs).length) return 0;
    return isPitcher
      ? Math.round((attrs.velocity + attrs.control + attrs.movement + (attrs.deception || 0) + (attrs.endurance || 50)) / 5)
      : Math.round((attrs.power + attrs.contact + attrs.speed + attrs.fielding + attrs.arm + attrs.discipline + attrs.endurance) / 7);
  }

  async function handleCreate() {
    setCreateError(null);
    const userId = localStorage.getItem('userId');
    if (!userId) {
      setCreateError('You must be signed in to create a player.');
      return;
    }
    setCreating(true);

    // Compute allocatedPoints: the difference between current attrs and base attrs
    const allocatedPoints: Record<string, number> = {};
    for (const attr of attrList) {
      const current = attrs[attr] || 0;
      const base = baseAttrs[attr] || 0;
      const allocated = current - base;
      if (allocated > 0) {
        allocatedPoints[attr] = allocated;
      }
    }

    try {
      const player = await createPlayer({
        userId, firstName, lastName, position,
        throwingHand, battingHand,
        age: 0,
        allocatedPoints,
        baseAttributes: baseAttrs,
        // Fallback fields for offline mode
        overall: getOverall(),
        attributes: attrs,
        face: generateRandomFace(),
      });

      try {
        const { autoOfferFromAITeams } = await import('../services/AIContractService');
        await autoOfferFromAITeams(player.id, position);
      } catch (e) {
        console.warn('AI offer failed:', e);
      }

      setCreatedPlayer(player);
      setCreated(true);
      // Advance to step 4 and start portrait generation
      setStep(4);
      loadPortraits(player.id, player.firstName, player.lastName, player.position, (player as any).gender);
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create player. Please try again.');
    } finally {
      setCreating(false);
    }
  }

  async function loadPortraits(playerId: string, fn: string, ln: string, pos: string, gender?: string) {
    setPortraitLoading(true);
    setPortraitError(null);
    setPortraitChoices([]);
    setSelectedSlot(null);
    try {
      const result = await generatePortraitTrio(
        playerId,
        `${fn} ${ln}`,
        pos,
        pos !== 'P',
        gender
      );
      setPortraitChoices(result.choices);
      setPortraitSlots(result.slots);
      if (result.errors && result.errors.length > 0) {
        setPortraitError(result.errors.join('; '));
      }
    } catch (err: any) {
      setPortraitError(err.message || 'Portrait generation failed');
    } finally {
      setPortraitLoading(false);
    }
  }

  async function handlePortraitSelect(index: number) {
    if (!createdPlayer) return;

    // First tap: just highlight the selection
    if (selectedSlot !== index) {
      setSelectedSlot(index);
      return;
    }

    // Second tap on same slot: confirm and save
    // Map the UI index to the actual backend slot number
    const backendSlot = portraitSlots[index];
    if (!backendSlot) return;

    setSelecting(true);
    try {
      const result = await selectPortrait(createdPlayer.id, backendSlot);
      setPortraitDone(true);
    } catch (err: any) {
      setPortraitError(err.message || 'Failed to save portrait');
    } finally {
      setSelecting(false);
    }
  }

  async function handleReroll() {
    if (!createdPlayer || rerollsUsed >= 1) return;
    setRerolling(true);
    setSelectedSlot(null);
    setRerollsUsed(prev => prev + 1);
    try {
      await loadPortraits(
        createdPlayer.id,
        createdPlayer.firstName,
        createdPlayer.lastName,
        createdPlayer.position,
        'common'
      );
    } finally {
      setRerolling(false);
    }
  }

  const overall = getOverall();
  const canProceedStep1 = !!(firstName.trim() && lastName.trim() && position);

  /* Helper to find which group a position belongs to */
  function getGroupForPos(posId: string): string {
    for (const g of POS_GROUPS) {
      if (g.positions.some(p => p.id === posId)) return g.label;
    }
    return 'Infield';
  }

  /* ─── Success: Auto-navigate to Agent Office ─── */
  React.useEffect(() => {
    if (portraitDone && createdPlayer) {
      onNavigate?.('/players');
    }
  }, [portraitDone]);

  if (portraitDone && createdPlayer) {
    return null;
  }

  /* ─── Main Form ─── */
  return (
    <div className="create-player-page">
      <div className="creation-card">
        {/* Stepper */}
        <div className="stepper">
          {[1, 2, 3, 4].map(s => (
            <React.Fragment key={s}>
              {s > 1 && <div className={`stepper-line ${step >= s ? 'active' : ''}`} />}
              <div className={`stepper-step ${step === s ? 'current' : step > s ? 'done' : ''}`}>
                <div className="stepper-dot">
                  {step > s ? <Check size={12} /> : s}
                </div>
                <span className="stepper-label">{STEP_LABELS[s - 1]}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* ─── Step 1: Identity ─── */}
        {step === 1 && (
          <div className="step-content" key="s1">
            <h2 className="step-title">Create Your Player</h2>
            <p className="step-sub">Name, position, and handedness</p>

            <div className="name-fields">
              <div className="field-group">
                <label className="field-label">First Name</label>
                <input
                  type="text"
                  className="field-input"
                  placeholder="First"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  maxLength={20}
                />
              </div>
              <div className="field-group">
                <label className="field-label">Last Name</label>
                <input
                  type="text"
                  className="field-input"
                  placeholder="Last"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  maxLength={20}
                />
              </div>
            </div>

            <div className="hand-selectors">
              <div className="hand-group">
                <span className="field-label">Bats</span>
                <div className="hand-toggles">
                  {['L', 'R', 'S'].map(h => (
                    <button
                      key={`bat-${h}`}
                      className={`hand-btn ${battingHand === h ? 'active' : ''}`}
                      onClick={() => setBattingHand(h)}
                    >
                      {h === 'L' ? 'L' : h === 'R' ? 'R' : 'S'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="hand-group">
                <span className="field-label">Throws</span>
                <div className="hand-toggles">
                  {['L', 'R'].map(h => (
                    <button
                      key={`throw-${h}`}
                      className={`hand-btn ${throwingHand === h ? 'active' : ''}`}
                      onClick={() => setThrowingHand(h)}
                    >
                      {h}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <p className="section-label">Position</p>

            <div className="pos-groups">
              {POS_GROUPS.map(group => (
                <div key={group.label} className="pos-group">
                  <span
                    className="pos-group-label"
                    style={{ color: GROUP_COLORS[group.label] }}
                  >
                    {group.label}
                  </span>
                  <div className="pos-row">
                    {group.positions.map(p => {
                      const isSelected = position === p.id;
                      const color = GROUP_COLORS[group.label];
                      return (
                        <button
                          key={p.id}
                          className={`pos-card ${isSelected ? 'selected' : ''}`}
                          style={{
                            '--pos-color': color,
                          } as React.CSSProperties}
                          onClick={() => handlePositionSelect(p.id)}
                        >
                          <span className="pos-card-abbr">{p.id}</span>
                          <span className="pos-card-name">{p.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <button
              className="btn-primary btn-full"
              onClick={() => setStep(2)}
              disabled={!canProceedStep1}
            >
              Next: Attributes <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ─── Step 2: Attributes ─── */}
        {step === 2 && (
          <div className="step-content" key="s2">
            <div className="attr-header">
              <h2 className="step-title">Attributes</h2>
              <button className="reroll-btn" onClick={reroll}>
                <RefreshCw size={14} /> Reroll
              </button>
            </div>

            <div className="overall-display">
              <span className="overall-label">OVR</span>
              <span className="overall-value" style={{ color: getGradeColor(overall) }}>{overall}</span>
              <span className="overall-grade" style={{ color: getGradeColor(overall) }}>{getGrade(overall)}</span>
              <div className={`points-pill ${points <= 3 ? 'low' : ''}`}>
                {points} pts
              </div>
            </div>

            <p className="step-sub">
              Tap arrows to adjust. +3 per tap costs 3 points. Max {MAX_PER_ATTR}.
            </p>

            <div className="attr-list">
              {attrList.map(attr => {
                const val = attrs[attr] || 30;
                const base = baseAttrs[attr] || 30;
                const canUp = points >= 3 && val + 3 <= MAX_PER_ATTR;
                const canDown = val > base;
                return (
                  <div key={attr} className="attr-row">
                    <span className="attr-name">{attrLabels[attr]}</span>
                    <span className="attr-grade" style={{ color: getGradeColor(val) }}>
                      {getGrade(val)}
                    </span>
                    <div className="attr-bar-track">
                      <div
                        className="attr-bar-fill"
                        style={{ width: `${val}%`, background: getGradeColor(val) }}
                      />
                    </div>
                    <span className="attr-val">{val}</span>
                    <div className="attr-btns">
                      <button
                        className="attr-adj"
                        onClick={() => adjustAttr(attr, 'up')}
                        disabled={!canUp}
                        aria-label={`Increase ${attrLabels[attr]}`}
                      >
                        <ChevronUp size={14} />
                      </button>
                      <button
                        className="attr-adj"
                        onClick={() => adjustAttr(attr, 'down')}
                        disabled={!canDown}
                        aria-label={`Decrease ${attrLabels[attr]}`}
                      >
                        <ChevronDown size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="step-nav">
              <button className="btn-ghost" onClick={() => setStep(1)}>
                <ArrowLeft size={16} /> Back
              </button>
              <button className="btn-primary" onClick={() => setStep(3)}>
                Next: Review <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ─── Step 3: Review ─── */}
        {step === 3 && (
          <div className="step-content" key="s3">
            <h2 className="step-title">Review & Create</h2>

            <div className="ballcard">
              {/* Top: OVR + Name + Position */}
              <div className="ballcard-top">
                <div className="ballcard-ovr" style={{ color: getGradeColor(overall) }}>
                  <span className="ballcard-ovr-num">{overall}</span>
                  <span className="ballcard-ovr-grade">{getGrade(overall)}</span>
                </div>
                <div className="ballcard-id">
                  <span className="ballcard-name">{firstName} {lastName}</span>
                  <span className="ballcard-pos" style={{ color: GROUP_COLORS[getGroupForPos(position)] }}>
                    {POSITIONS_FLAT.find(p => p.id === position)?.label || position}
                  </span>
                </div>
              </div>

              {/* Middle: Avatar + Hands */}
              <div className="ballcard-mid">
                <div className="ballcard-avatar">
                  {firstName[0]}{lastName[0]}
                </div>
                <div className="ballcard-hands">
                  <div className="ballcard-hand-item">
                    <span className="ballcard-hand-label">Bats</span>
                    <span className="ballcard-hand-val">{battingHand === 'S' ? 'Switch' : battingHand === 'L' ? 'Left' : 'Right'}</span>
                  </div>
                  <div className="ballcard-hand-item">
                    <span className="ballcard-hand-label">Throws</span>
                    <span className="ballcard-hand-val">{throwingHand === 'L' ? 'Left' : 'Right'}</span>
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="ballcard-stats">
                {attrList.map(attr => {
                  const val = attrs[attr] || 30;
                  return (
                    <div key={attr} className="ballcard-stat">
                      <span className="bcs-label">{attrLabels[attr]}</span>
                      <span className="bcs-val" style={{ color: getGradeColor(val) }}>{val}</span>
                      <span className="bcs-grade" style={{ color: getGradeColor(val) }}>{getGrade(val)}</span>
                    </div>
                  );
                })}
              </div>

              {/* Footer: Cost */}
              <div className="ballcard-footer">
                <Coins size={14} />
                <span>500 $BALLS to mint</span>
              </div>
            </div>

            <div className="review-actions">
              {createError && (
                <div className="create-error">{createError}</div>
              )}
              <button
                className="btn-gold btn-full"
                onClick={handleCreate}
                disabled={creating}
              >
                {creating ? (
                  <><span className="spinner" /> Creating...</>
                ) : (
                  <><CircleDot size={18} /> Create Player</>
                )}
              </button>
              <div className="review-actions-row">
                <button className="btn-ghost btn-small" onClick={() => setStep(2)}>
                  <ArrowLeft size={14} /> Back
                </button>
                <button className="btn-danger btn-small" onClick={() => onNavigate?.('players')}>
                  <X size={14} /> Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── Step 4: Choose Your Look ─── */}
        {step === 4 && (
          <div className="step-content" key="s4">
            <PortraitPicker
              choices={portraitChoices}
              loading={portraitLoading}
              canReroll={rerollsUsed < 1}
              rerolling={rerolling}
              onSelect={handlePortraitSelect}
              onReroll={handleReroll}
              onSkip={() => onNavigate?.('/players')}
              selectedSlot={selectedSlot}
              selecting={selecting}
              error={portraitError}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatePlayerPage;
